/**
 * Hazır plan şablonları — Plan Modu "Hazır Şablon" akışı.
 *
 * Şablon seçilince panel değişkenleri TEK formda sorar (örn. şube adı bir kez
 * sorulur) ve görev komutlarındaki {degisken} yer tutucuları planPromptBuilder
 * ile her göreve enjekte edilir — AI aynı bilgiyi tekrar sormaz.
 *
 * Komut şablonu MANUAL_COMPLETION_MARKER (planPromptBuilder) ile başlıyorsa görev
 * "yarı otomatik"tir: AI yönlendirir, işi kullanıcı yapar; görev bitince
 * 'needsReview' durumuna düşer ve kullanıcı panelden "Tamamlandı işaretle" der.
 */

export interface PlanTemplateVariable {
  /** Görev komutlarında {key} olarak kullanılır. */
  key: string;
  /** Formda gösterilen Türkçe etiket. */
  label: string;
  /** true ise plan oluşturulmadan önce doldurulması zorunludur. */
  required: boolean;
}

export interface PlanTemplateTask {
  title: string;
  promptTemplate: string;
}

export interface PlanTemplate {
  /** Backend'de AiPlans.TemplateKey olarak saklanır. */
  key: string;
  title: string;
  description: string;
  variables: PlanTemplateVariable[];
  tasks: PlanTemplateTask[];
}

/**
 * Yeni Şube Açılışı — şube + personeller + yönetici + kasa/banka + hızlı satış
 * ekranı + ödeme yöntemleri + personel/kasiyer atama + depo-stok ve fiyat
 * aktarımı + opsiyonel mutfak entegrasyonu. Manuel iş akışının birebir plan
 * karşılığı; AI her görevde eksik bilgiyi chat'te sorar, varsaymaz.
 */
const ASK_DONT_ASSUME =
  ' Eksik bilgileri bana chat üzerinden sor; hiçbir bilgiyi varsayma.';

const BRANCH_OPENING_TEMPLATE: PlanTemplate = {
  key: 'branch-opening',
  title: 'Yeni Şube Açılışı',
  description:
    'Şube, personeller, kasa/banka, hızlı satış ekranı, ödeme yöntemleri, kasiyerler, depo/stok ve fiyat aktarımına kadar tüm açılış adımları. AI gerekli bilgileri sohbette sorar.',
  variables: [{ key: 'branchName', label: 'Yeni şube adı', required: true }],
  tasks: [
    {
      title: 'Şube oluştur',
      promptTemplate:
        'createBranch eylemiyle "{branchName}" adında yeni bir şube oluştur. ' +
        'Önce adres ve telefon bilgilerini bana sor; vermezsem boş bırakabilirsin.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Şube personellerini oluştur (3 kişi)',
      promptTemplate:
        '"{branchName}" şubesi için 3 personel hesabı oluşturacağız: (1) hızlı satış kasiyeri, ' +
        '(2) paket sipariş kasiyeri, (3) şube yöneticisi (role=manager). Her biri için bana sırayla ' +
        'ad-soyad, e-posta ve şifre (en az 6 karakter) sor; ardından createPersonnel eylemiyle ' +
        'branchName="{branchName}" vererek oluştur. Üçü de bitince oluşturulan hesapları özetle.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Yöneticiyi şubeye ata ve panel yetkilerini düzenle',
      promptTemplate:
        'Bir önceki adımda oluşturulan yönetici personeli assignBranchManager ile "{branchName}" ' +
        'şubesine yönetici olarak ata (adı bağlamda yoksa bana sor; listPersonnel ile doğrula). ' +
        'Atamadan sonra hangi panel modüllerinin açılacağını bana sor ve setManagerPermissions ile ayarla.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Kasa ve banka hesaplarını oluştur',
      promptTemplate:
        '"{branchName}" şubesi için kasa ve banka hesaplarını oluşturacağız. Önce bana kaç kasa ve kaç ' +
        'banka/POS hesabı istediğimi ve adlarını sor (kasa için önerin "{branchName} Kasa" olsun). ' +
        'Kasaları createCashbox ile branchName="{branchName}" vererek, POS hesaplarını createBankAccount ' +
        '(isPosAccount=true; banka adı, hesap no, IBAN bilgilerini sorarak) ile companyBranchName="{branchName}" ' +
        'vererek oluştur — böylece hepsi şubeye atanmış olur. Yemek kartları (Multinet vb.) için ayrı bir kasa ' +
        'isteyip istemediğimi de sor.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Hızlı satış ekranı oluştur',
      promptTemplate:
        'createQuickSaleScreen ile "{branchName}" şubesine bağlı yeni bir hızlı satış ekranı oluştur. ' +
        'Ekran adını bana sor — önerin "{branchName}" olsun.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Ödeme yöntemlerini ekle ve bağla',
      promptTemplate:
        '"{branchName}" ekranının ayar modalını aç (openQuickSaleSettings) ve Ödeme Yöntemleri sekmesine geç. ' +
        'listQuickSalePaymentBindings ile kasa/POS seçeneklerini gör, sonra bana hangi ödeme yöntemlerini ' +
        'istediğimi sor: nakit → hangi kasaya, POS → hangi banka hesabına, yemek kartı (MULTINET, SETCARD vb.) ' +
        '→ hangi kasaya bağlanacak. addQuickSalePaymentOption ile hepsini ekle ve bitince saveQuickSaleSettings ' +
        'ile kaydet.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Personelleri ekrana ata',
      promptTemplate:
        '"{branchName}" ekranının ayar modalını aç (openQuickSaleSettings), listQuickSaleAssignablePersonnel ' +
        'ile personelleri listele ve daha önce oluşturduğumuz şube personellerini setQuickSaleAssignedPersonnel ' +
        'ile ekrana ata (hangilerinin atanacağını bana onaylat). Bitince saveQuickSaleSettings ile kaydet.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Kasiyer oluştur',
      promptTemplate:
        '"{branchName}" ekranı için kasiyer(ler) oluşturacağız. Bana kasiyer adlarını ve 4-6 haneli PIN\'lerini ' +
        'sor, ardından createScreenCashier (screenType=QuickSale) ile oluştur. Yetkilerde değişiklik isteyip ' +
        'istemediğimi de sor (varsayılanlar güvenlidir). Kasiyer girişi için ekranın "Kasiyer Sistemi" ayarının ' +
        'açık olması gerektiğini kontrol et; kapalıysa updateQuickSaleSettings ile aç ve kaydet.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Kaynak ekrandan depo/stok taslağı kaydet',
      promptTemplate:
        'Bana ürünlerin/depo düzeninin hangi MEVCUT satış ekranından alınacağını sor (listQuickSaleScreens ile ' +
        'ekranları listeleyebilirsin). Seçilen KAYNAK ekranın ayar modalını aç (openQuickSaleSettings), ' +
        'saveWarehouseStockTemplate ile "{branchName}" adıyla taslak kaydet ve modalı closeQuickSaleSettings ' +
        'ile KAYDETMEDEN kapat (kaynak ekranın ayarları değişmemeli). Taslak bu tarayıcıda saklanır — sonraki ' +
        'görevde yeni ekrana uygulanacak.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Taslağı yeni ekrana uygula ve şube fiyatlarını aktar',
      promptTemplate:
        '"{branchName}" ekranının ayar modalını aç (openQuickSaleSettings), listWarehouseStockTemplates ile ' +
        'taslakları kontrol et ve önceki görevde kaydedilen "{branchName}" taslağını applyWarehouseStockTemplate ' +
        'ile uygula; saveQuickSaleSettings ile kaydet. Ardından transferBranchPrices ile kaynak şubenin ' +
        'fiyatlarını "{branchName}" şubesine kopyala (kaynak şube adını bilmiyorsan bana sor) ve ' +
        'kopyalanan/atlanan/başarısız sayılarını raporla.' +
        ASK_DONT_ASSUME,
    },
    {
      title: 'Mutfak entegrasyonu (isteğe bağlı)',
      promptTemplate:
        'Bana "{branchName}" ekranında mutfak ekranı entegrasyonu isteyip istemediğimi sor. İstemezsem bu ' +
        'görevi kısaca kapat. İstersem: listQrMenus ile QR menüleri listele, uygun menü yoksa createQrMenu ile ' +
        'oluşturmayı öner; sonra ekranın ayar modalını aç (openQuickSaleSettings), setKitchenIntegration ile ' +
        'mutfağı açıp seçilen QR menüyü bağla ve saveQuickSaleSettings ile kaydet.' +
        ASK_DONT_ASSUME,
    },
  ],
};

/** Panelde sunulan hazır şablonlar (sıra menü sırasıdır). */
export const PLAN_TEMPLATES: PlanTemplate[] = [BRANCH_OPENING_TEMPLATE];

export const findPlanTemplate = (key: string): PlanTemplate | undefined =>
  PLAN_TEMPLATES.find((tpl) => tpl.key === key);
